import { lazy, Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { Panel } from '../../panels/panel';
import { PanelTitle } from '../../panels/panel-title';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { useGetCadEventOptions } from '../../federated-search/hooks/use-get-cad-event-options';
import { useFederatedSearchActions } from '../../federated-search/federated-search-context';
import { FOCUSED_FIELD_BY_QUERYABLE_ENTITY_TYPE } from './constants';
import { getUniversalSearchFormLabel } from '~/common/helpers/get-usx-labels';

const FederatedSearchQueryForm = lazy(() =>
    import('federated-search/FederatedSearchQueryForm').then((module) => ({
        default: module.FederatedSearchQueryForm,
    }))
);

/**
 *   Displays the Universal Search inquiry form.  This page only displayed when `dexNextEnabled` is true.
 */
export const DexNextFormPanel = observer(function DexNextFormPanel({
    panel,
}: {
    panel: WorkspacePanel;
}): React.ReactElement {
    const dexNextFormPrefill = panel.getPanelState('dexNextFormPrefill');

    const { setQueryFormValues, setSelectedForm } = useFederatedSearchActions() || {};
    const { getCadEventOptions } = useGetCadEventOptions();

    const entityType = dexNextFormPrefill?.entityType;
    const prefillValues = dexNextFormPrefill?.prefillValues;

    useEffect(() => {
        if (setSelectedForm && entityType) {
            setSelectedForm(entityType, FOCUSED_FIELD_BY_QUERYABLE_ENTITY_TYPE[entityType]);
        }
    }, [setSelectedForm, entityType]);

    useEffect(() => {
        if (setQueryFormValues && prefillValues) {
            setQueryFormValues(prefillValues);
        }
    }, [setQueryFormValues, prefillValues]);

    return (
        <Panel
            panel={panel}
            header={<PanelTitle>{getUniversalSearchFormLabel(true)}</PanelTitle>}
            body={
                <Suspense fallback={<LoadingIndicator />}>
                    <FederatedSearchQueryForm
                        key={`${entityType ?? ''}-${dexNextFormPrefill?.eventNumber ?? ''}`}
                        applicationScope="CAD_DISPATCH"
                        getCadEventOptions={(searchTerm) =>
                            getCadEventOptions(searchTerm, dexNextFormPrefill?.eventNumber)
                        }
                    />
                </Suspense>
            }
        />
    );
});
